/**
 * Market Configuration
 *
 * Centralized configuration for market-specific features and behavior.
 * Each market can have the following properties:
 *
 * @property {string} key - Unique market identifier
 * @property {string} label - Short label for market tabs (e.g., "H", "RBI")
 * @property {string} name - Full display name
 * @property {boolean} isMainline - Whether this is a mainline market (h2h, totals, spreads)
 * @property {boolean} excludeFromMaster - Whether to exclude from master market filter
 * @property {boolean} showTiers - Whether to show tier filtering for this market
 * @property {boolean} hasDropdown - Whether this market supports dropdown grouping (e.g., NFL TDs by point value)
 * @property {string} type - Market type: 'over_under', 'binary', 'mainline'
 * @property {boolean} teamGrouping - Whether to group players by team
 */

// MLB Markets
const MLB_MARKETS = [
  // Mainline Markets
  {
    key: 'h2h',
    label: 'ML',
    name: 'Mainlines',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'totals',
    label: 'O/U',
    name: 'Totals',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'spreads',
    label: 'RL',
    name: 'Run Line',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },

  // Player Markets
  {
    key: 'batter_hits',
    label: 'H',
    name: 'Hits',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'batter_rbis',
    label: 'RBI',
    name: 'RBIs',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'batter_runs_scored',
    label: 'R',
    name: 'Runs',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'batter_hits_runs_rbis',
    label: 'H+R+R',
    name: 'Hits + Runs + RBIs',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'batter_home_runs',
    label: 'HR',
    name: 'Home Runs',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'batter_doubles',
    label: '2B',
    name: 'Doubles',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'batter_triples',
    label: '3B',
    name: 'Triples',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'batter_total_bases',
    label: 'TB',
    name: 'Total Bases',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'batter_stolen_bases',
    label: 'SB',
    name: 'Stolen Bases',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'pitcher_strikeouts',
    label: 'K',
    name: 'Strikeouts',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'pitcher_hits_allowed',
    label: 'Hits',
    name: 'Hits Allowed',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'pitcher_outs',
    label: 'Outs',
    name: 'Outs',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  }
];

// Basketball Markets (shared by WNBA and NBA)
const BASKETBALL_MARKETS = [
  // Mainline Markets
  {
    key: 'h2h',
    label: 'ML',
    name: 'Mainlines',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'totals',
    label: 'O/U',
    name: 'Totals',
    isMainline: true,
    excludeFromMaster: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'spreads',
    label: '+/-',
    name: 'Spread',
    isMainline: true,
    excludeFromMaster: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },

  // Player Markets
  {
    key: 'player_points',
    label: 'PTS',
    name: 'Points',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_rebounds',
    label: 'REB',
    name: 'Rebounds',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_assists',
    label: 'AST',
    name: 'Assists',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_threes',
    label: '3PM',
    name: 'Threes',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_first_basket',
    label: '1st',
    name: 'First Basket',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  },
  {
    key: 'player_first_team_basket',
    label: '1st Tm',
    name: 'First Team Basket',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  },
  {
    key: 'player_double_double',
    label: 'DD',
    name: 'Double Double',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  },
  {
    key: 'player_triple_double',
    label: 'TD',
    name: 'Triple Double',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  },
  {
    key: 'player_points_rebounds_assists',
    label: 'PRA',
    name: 'Points + Rebounds + Assists',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_points_rebounds',
    label: 'P+R',
    name: 'Points + Rebounds',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_points_assists',
    label: 'P+A',
    name: 'Points + Assists',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_rebounds_assists',
    label: 'R+A',
    name: 'Rebounds + Assists',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  }
];

// WNBA Markets (using shared basketball markets)
const WNBA_MARKETS = BASKETBALL_MARKETS;

// NBA Markets (using shared basketball markets)
const NBA_MARKETS = BASKETBALL_MARKETS;

// Soccer Markets
const SOCCER_MARKETS = [
  {
    key: 'h2h_3_way',
    label: 'Game',
    name: 'Game',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'btts',
    label: 'BTTS',
    name: 'Both Teams To Score',
    showTiers: false,
    hasDropdown: false,
    type: 'yes_no',
    teamGrouping: false
  },
  {
    key: 'totals_corners',
    label: 'Corners',
    name: 'Total Corners',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: false
  },
  {
    key: 'team_totals_corners',
    label: 'Tm Corners',
    name: 'Team Total Corners',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_goal_scorer_anytime',
    label: 'Goal',
    name: 'Anytime Goal Scorer',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  },
  {
    key: 'player_shots',
    label: 'SHO',
    name: 'Shots',
    showTiers: true,
    hasDropdown: true,
    defaultPoint: 0.5,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_cards',
    label: 'Cards',
    name: 'Cards',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_assists',
    label: 'AST',
    name: 'Assists',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_shots_on_target',
    label: 'SOT',
    name: 'Shots on Target',
    showTiers: true,
    hasDropdown: true,
    defaultPoint: 0.5,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_tackles',
    label: 'TCK',
    name: 'Tackles',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_first_goal_scorer',
    label: '1st Goal',
    name: 'First Goal Scorer',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  },
  {
    key: 'player_last_goal_scorer',
    label: 'Last Goal',
    name: 'Last Goal Scorer',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  }
];

// NCAAF Markets (only mainline markets)
const NCAAF_MARKETS = [
  {
    key: 'h2h',
    label: 'Game',
    name: 'Game',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'h2h_quarters',
    label: 'Quarters',
    name: 'Quarters',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'h2h_halves',
    label: 'Halves',
    name: 'Halves',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'h2h_all',
    label: 'All',
    name: 'All',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  }
];

// NFL Markets
const NFL_MARKETS = [
  // Mainline Markets
  {
    key: 'h2h',
    label: 'ML',
    name: 'Mainlines',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'totals',
    label: 'O/U',
    name: 'Totals',
    isMainline: true,
    excludeFromMaster: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'spreads',
    label: '+/-',
    name: 'Spread',
    isMainline: true,
    excludeFromMaster: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },

  // Player Markets - Touchdowns
  {
    key: 'player_1st_td',
    label: '1st',
    name: 'First Touchdown',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  },
  {
    key: 'player_last_td',
    label: 'Last',
    name: 'Last Touchdown',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  },
  {
    key: 'player_tds_over',
    label: 'TDS',
    name: 'Total Touchdowns',
    showTiers: true,
    hasDropdown: true,  // This market has dropdown grouping by point value
    type: 'binary',
    teamGrouping: true
  },

  // Player Markets - Passing
  {
    key: 'player_pass_tds',
    label: 'PaTDS',
    name: 'Pass Touchdowns',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_pass_yds',
    label: 'PaYds',
    name: 'Pass Yards',
    showTiers: false,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },

  // Player Markets - Rushing
  {
    key: 'player_rush_yds',
    label: 'RuYds',
    name: 'Rush Yards',
    showTiers: false,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_rush_attempts',
    label: 'RuAtt',
    name: 'Rush Attempts',
    showTiers: false,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },

  // Player Markets - Receiving
  {
    key: 'player_receptions',
    label: 'REC',
    name: 'Receptions',
    showTiers: false,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_reception_yds',
    label: 'ReYds',
    name: 'Reception Yards',
    showTiers: false,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },

  // Player Markets - Combo
  {
    key: 'player_rush_reception_yds',
    label: 'Ru+RecYds',
    name: 'Rush + Rec Yards',
    showTiers: false,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },

  // Player Markets - Kicking
  {
    key: 'player_field_goals',
    label: 'FG',
    name: 'Field Goals Made',
    showTiers: false,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  }
];

// NHL Markets
const NHL_MARKETS = [
  // Mainline Markets
  {
    key: 'h2h',
    label: 'Game',
    name: 'Game',
    isMainline: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'h2h_periods',
    label: 'Periods',
    name: 'Periods',
    isMainline: true,
    excludeFromMaster: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },
  {
    key: 'h2h_all',
    label: 'All',
    name: 'All',
    isMainline: true,
    excludeFromMaster: true,
    showTiers: false,
    hasDropdown: false,
    type: 'mainline',
    teamGrouping: false
  },

  // Player Markets - Binary
  {
    key: 'player_goal_scorer_first',
    label: '1st',
    name: 'First Goal Scorer',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  },
  {
    key: 'player_goal_scorer_last',
    label: 'Last',
    name: 'Last Goal Scorer',
    showTiers: false,
    hasDropdown: false,
    type: 'binary',
    teamGrouping: true
  },

  // Player Markets - Over/Under
  {
    key: 'player_goals',
    label: 'G',
    name: 'Goals',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_points',
    label: 'Pts',
    name: 'Points',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_assists',
    label: 'A',
    name: 'Assists',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_shots_on_goal',
    label: 'SOG',
    name: 'Shots on Goal',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  },
  {
    key: 'player_total_saves',
    label: 'Sv',
    name: 'Saves',
    showTiers: true,
    hasDropdown: false,
    type: 'over_under',
    teamGrouping: true
  }
];

// Sport-specific market mapping
export const SPORT_MARKETS = {
  mlb: MLB_MARKETS,
  nba: NBA_MARKETS,
  wnba: WNBA_MARKETS,
  soccer: SOCCER_MARKETS,
  ncaaf: NCAAF_MARKETS,
  nfl: NFL_MARKETS,
  nhl: NHL_MARKETS
};

/**
 * Get market configuration for a specific market key
 * @param {string} sport - Sport key ('mlb', 'nfl', 'nhl', etc.)
 * @param {string} marketKey - Market key to look up
 * @returns {Object|null} Market configuration object or null if not found
 */
export const getMarketConfig = (sport, marketKey) => {
  const markets = SPORT_MARKETS[sport?.toLowerCase()];
  if (!markets) return null;
  return markets.find(m => m.key === marketKey) || null;
};

/**
 * Check if a market should show tier filtering
 * @param {string} sport - Sport key
 * @param {string} marketKey - Market key
 * @returns {boolean} True if tiers should be shown
 */
export const shouldShowTiers = (sport, marketKey) => {
  const config = getMarketConfig(sport, marketKey);
  return config?.showTiers ?? false;
};

/**
 * Check if a market has dropdown grouping
 * @param {string} sport - Sport key
 * @param {string} marketKey - Market key
 * @returns {boolean} True if market has dropdown
 */
export const hasDropdown = (sport, marketKey) => {
  const config = getMarketConfig(sport, marketKey);
  return config?.hasDropdown ?? false;
};

/**
 * Get market type
 * @param {string} sport - Sport key
 * @param {string} marketKey - Market key
 * @returns {string} Market type ('over_under', 'binary', 'mainline')
 */
export const getMarketType = (sport, marketKey) => {
  const config = getMarketConfig(sport, marketKey);
  return config?.type || 'over_under';
};

/**
 * Check if a market should group players by team
 * @param {string} sport - Sport key
 * @param {string} marketKey - Market key
 * @returns {boolean} True if should group by team
 */
export const shouldGroupByTeam = (sport, marketKey) => {
  const config = getMarketConfig(sport, marketKey);
  return config?.teamGrouping ?? true;
};

/**
 * Get the default point value for a market
 * @param {string} sport - Sport key
 * @param {string} marketKey - Market key
 * @returns {number|null} Default point value or null if not specified
 */
export const getDefaultPoint = (sport, marketKey) => {
  const config = getMarketConfig(sport, marketKey);
  return config?.defaultPoint ?? null;
};
